<template>
  <InertiaHead>
    <title>Privacy Policy</title>
    <meta
      name="description"
      content="Learn how VibeTeams protects your privacy. Our privacy policy explains how we collect, use, and safeguard your data when using our AI collaboration platform."
    />
    <link rel="canonical" href="https://vibeteams.ai/privacy-policy" />
  </InertiaHead>

  <div class="bg-landing relative bg-cover bg-fixed bg-center bg-no-repeat">
    <!-- Navigation -->
    <nav class="fixed top-0 z-10 w-full" :class="{ 'backdrop-blur-md': isScrolled }">
      <div class="mx-auto max-w-7xl px-4 py-4 sm:px-6 lg:px-8">
        <InertiaLink href="/" class="inline-block">
          <img src="@images/logo/vibeteams-logo-secondary.svg" alt="VibeTeams Logo" class="h-8" />
        </InertiaLink>
      </div>
    </nav>

    <!-- Content -->
    <div class="relative mx-auto max-w-4xl px-4 pt-[120px] pb-12 sm:px-6 lg:px-8">
      <div class="animate-fade-in space-y-8">
        <!-- Header -->
        <div class="space-y-4">
          <h1 class="font-exo-bold text-4xl text-white">Privacy Policy</h1>
          <p class="text-eerie-black-200 italic">Last Updated: October 2025</p>
        </div>

        <!-- Content Sections -->
        <div class="prose max-w-none space-y-8 divide-y divide-eerie-black-400 text-gray-300 prose-invert">
          <!-- Introduction -->
          <section class="space-y-4">
            <h2 class="font-exo-bold text-2xl text-white">1. Introduction</h2>
            <p>
              Welcome to <span class="font-segoe-bold text-white">VibeTeams.ai</span>, operated by
              <span class="font-segoe-bold text-white">CoreProc, Inc.</span> (“we,” “us,” or “our”).
            </p>
            <p>
              This Privacy Policy explains how we collect, use, share, and protect your personal information when you use our AI-powered collaboration
              platform (the “Service”).
            </p>
            <p>
              We are committed to safeguarding your privacy and maintaining transparency about our data practices. By using the Service, you agree to
              this Privacy Policy and our Terms of Service.
            </p>
          </section>

          <!-- Information We Collect -->
          <section class="space-y-4">
            <h2 class="font-exo-bold text-2xl text-white">2. Information We Collect</h2>

            <h3 class="font-segoe-bold text-xl text-white">2.1 Information You Provide</h3>
            <p>We collect information you provide directly to us, including:</p>
            <ul class="list-disc space-y-2 pl-6">
              <li><strong>Account Information:</strong> Name, email address, and password when you register</li>
              <li><strong>Profile Details:</strong> Optional preferences, team roles, and display names</li>
              <li><strong>AI Agent Configurations:</strong> Custom agent names, system prompts, and parameters</li>
              <li><strong>Chat Content:</strong> Messages you send to AI agents and their responses</li>
              <li><strong>Team Data:</strong> Team names, descriptions, and organizational structures</li>
              <li>
                <strong>Payment Information:</strong> billing data is processed securely through our
                <span class="font-segoe-bold text-white">third-party payment providers</span>. We do
                <span class="font-segoe-bold text-white">not store or have access to full credit card details</span> — all transactions are handled
                and secured by these providers in compliance with PCI-DSS standards.
              </li>
              <li><strong>Support Requests:</strong> Messages and attachments you submit to our support channels</li>
            </ul>

            <h3 class="font-segoe-bold text-xl text-white">2.2 Information Collected Automatically</h3>
            <p>When you use the Service, we automatically collect:</p>
            <ul class="list-disc space-y-2 pl-6">
              <li><strong>Usage Data:</strong> Features used, session duration, frequency, and interaction patterns</li>
              <li><strong>Device Information:</strong> Browser type, operating system, device identifiers</li>
              <li><strong>Log Data:</strong> IP address, timestamps, and referring URLs</li>
              <li><strong>Analytics Data:</strong> Aggregated performance and behavioral metrics</li>
              <li><strong>Token Usage:</strong> Credit consumption, AI model usage, and API call volume</li>
            </ul>

            <h3 class="font-segoe-bold text-xl text-white">2.3 Information from Third Parties</h3>
            <ul class="list-disc space-y-2 pl-6">
              <li><strong>Social Logins:</strong> Basic profile info from Google or other providers</li>
              <li><strong>Payment Processors:</strong> Transaction details from third-party providers</li>
            </ul>
          </section>

          <!-- How We Use Your Information -->
          <section class="space-y-4">
            <h2 class="font-exo-bold text-2xl text-white">3. How We Use Your Information</h2>

            <h3 class="font-segoe-bold text-xl text-white">3.1 Service Provision and Improvement</h3>
            <ul class="list-disc space-y-2 pl-6">
              <li>Operate, maintain, and improve the Service</li>
              <li>Enable AI agent interactions and store configurations</li>
              <li>Analyze usage trends to enhance experience and reliability</li>
              <li>Develop new features and research product performance</li>
            </ul>

            <h3 class="font-segoe-bold text-xl text-white">3.2 Account and Billing Management</h3>
            <ul class="list-disc space-y-2 pl-6">
              <li>Create, authenticate, and manage your account</li>
              <li>Process payments and renew subscriptions</li>
              <li>Track credit or token usage</li>
              <li>Send important account updates</li>
            </ul>

            <h3 class="font-segoe-bold text-xl text-white">3.3 Communication</h3>
            <ul class="list-disc space-y-2 pl-6">
              <li>Respond to inquiries and provide support</li>
              <li>Send product updates, announcements, and security alerts</li>
              <li>Share marketing content with your consent</li>
            </ul>

            <h3 class="font-segoe-bold text-xl text-white">3.4 Security and Compliance</h3>
            <ul class="list-disc space-y-2 pl-6">
              <li>Detect and prevent fraud or abuse</li>
              <li>Enforce Terms of Service and legal requirements</li>
              <li>Protect our rights, property, and users</li>
            </ul>
          </section>

          <!-- Sharing Your Information -->
          <section class="space-y-4">
            <h2 class="font-exo-bold text-2xl text-white">4. How We Share Your Information</h2>

            <h3 class="font-segoe-bold text-xl text-white">4.1 AI Model Providers</h3>
            <p>
              When you interact with AI agents, your prompts, messages, and related context may be securely transmitted to
              <span class="font-segoe-bold text-white">trusted third-party AI model providers</span> that process data to generate responses. These
              providers operate cloud-based AI systems and act as <span class="font-segoe-bold text-white">data processors</span> on our behalf.
            </p>
            <p>
              They process your information <span class="font-segoe-bold text-white">only for the purpose of generating AI outputs</span>, in
              accordance with our instructions and applicable data-protection laws. We ensure that all such providers maintain
              <span class="font-segoe-bold text-white">appropriate technical and organizational safeguards</span>, comply with recognized security
              standards, and are bound by <span class="font-segoe-bold text-white">data-processing agreements</span> consistent with the GDPR.
            </p>

            <h3 class="font-segoe-bold text-xl text-white">4.2 Payment Processors</h3>
            <p>
              Payments are handled by <span class="font-segoe-bold text-white">third-party payment providers</span> who process card and transaction
              data securely in accordance with PCI-DSS standards. We do not store or access full credit-card details.
            </p>

            <h3 class="font-segoe-bold text-xl text-white">4.3 Service Providers</h3>
            <p>
              We engage trusted <span class="font-segoe-bold text-white">third-party service providers</span> to help us operate, maintain, and
              improve the Service. These may include:
            </p>
            <ul class="list-disc space-y-2 pl-6">
              <li><span class="font-segoe-bold text-white">Cloud infrastructure providers</span> that host and store data securely</li>
              <li><span class="font-segoe-bold text-white">Analytics providers</span> that help us understand platform usage and performance</li>
              <li><span class="font-segoe-bold text-white">Customer support providers</span> that assist in resolving user inquiries</li>
              <li><span class="font-segoe-bold text-white">Email and communication providers</span> that deliver notifications and updates</li>
            </ul>
            <p>
              These providers act as <span class="font-segoe-bold text-white">data processors</span> on our behalf. They are contractually required to
              implement appropriate security measures, process data only as instructed, and comply with applicable data protection laws, including the
              GDPR.
            </p>

            <h3 class="font-segoe-bold text-xl text-white">4.4 Legal and Regulatory Disclosures</h3>
            <p>We may disclose information when required by law or to:</p>
            <ul class="list-disc space-y-2 pl-6">
              <li>Respond to legal process (e.g., court orders)</li>
              <li>Comply with regulatory obligations</li>
              <li>Protect the rights and safety of users and our platform</li>
            </ul>

            <h3 class="font-segoe-bold text-xl text-white">4.5 Business Transfers</h3>
            <p>
              In case of merger, acquisition, or asset sale, user data may transfer as part of the transaction. We will notify you of any material
              changes to ownership or use.
            </p>

            <h3 class="font-segoe-bold text-xl text-white">4.6 With Your Consent</h3>
            <p>We may share data for other purposes only with your explicit consent.</p>
          </section>

          <!-- Data Storage and Security -->
          <section class="space-y-4">
            <h2 class="font-exo-bold text-2xl text-white">5. Data Storage and Security</h2>

            <ul class="list-disc space-y-2 pl-6">
              <li>Data is stored on secure servers managed by reputable infrastructure providers.</li>
              <li>Encryption in transit (HTTPS/TLS) and at rest.</li>
              <li>Regular security audits, access controls, and employee training.</li>
              <li>Incident-response plans for potential breaches.</li>
            </ul>

            <h3 class="font-segoe-bold text-xl text-white">Data Retention</h3>
            <table>
              <thead>
                <tr>
                  <th>Data Type</th>
                  <th>Retention Period</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>Account Data</td>
                  <td>Until account deletion</td>
                </tr>
                <tr>
                  <td>Chat History</td>
                  <td>Until user deletion or account closure</td>
                </tr>
                <tr>
                  <td>Usage Data</td>
                  <td>Up to 24 months for analytics</td>
                </tr>
                <tr>
                  <td>Billing Records</td>
                  <td>As required by law (typically 7 years)</td>
                </tr>
              </tbody>
            </table>
            <p>After account deletion, data may persist in encrypted backups for up to 90 days before permanent purge.</p>
          </section>

          <!-- User Rights -->
          <section class="space-y-4">
            <h2 class="font-exo-bold text-2xl text-white">6. Your Rights and Choices</h2>

            <p>You may:</p>
            <ul class="list-disc space-y-2 pl-6">
              <li><span class="font-segoe-bold text-white">Access and Update</span> your profile information via account settings.</li>
              <li><span class="font-segoe-bold text-white">Export Your Data</span> by contacting hello@vibeteams.ai.</li>
              <li><span class="font-segoe-bold text-white">Delete Your Account</span> to erase personal and chat data.</li>
              <li><span class="font-segoe-bold text-white">Manage Marketing Preferences</span> or unsubscribe from emails.</li>
              <li>
                <span class="font-segoe-bold text-white">Exercise Regional Rights</span> under GDPR, CCPA, or other laws (e.g., access, erasure,
                portability).
              </li>
            </ul>
          </section>

          <!-- Cookies and Tracking -->
          <section class="space-y-4">
            <h2 class="font-exo-bold text-2xl text-white">7. Cookies and Tracking Technologies</h2>

            <p>We use cookies to:</p>
            <ul class="list-disc space-y-2 pl-6">
              <li>Keep you logged in and save preferences.</li>
              <li>Analyze usage patterns and performance.</li>
              <li>Improve security and prevent fraud.</li>
              <li>Measure marketing effectiveness.</li>
            </ul>

            <p>You can control cookies through your browser settings. Disabling cookies may affect Service functionality.</p>
          </section>

          <!-- International Transfers -->
          <section class="space-y-4">
            <h2 class="font-exo-bold text-2xl text-white">8. International Data Transfers</h2>
            <p>
              Your data may be processed in countries other than your own. We use standard contractual clauses and other legal mechanisms to ensure
              adequate protection.
            </p>
          </section>

          <!-- Children's Privacy -->
          <section class="space-y-4">
            <h2 class="font-exo-bold text-2xl text-white">9. Children's Privacy</h2>
            <p>The Service is not intended for users under 18. If we learn we’ve collected data from a minor, we’ll delete it promptly.</p>
          </section>

          <!-- Third-Party Links -->
          <section class="space-y-4">
            <h2 class="font-exo-bold text-2xl text-white">10. Third-Party Links and Services</h2>
            <p>
              Our Service may link to external websites not operated by us. We are not responsible for their privacy practices. Please review those
              policies separately.
            </p>
          </section>

          <!-- Changes to Policy -->
          <section class="space-y-4">
            <h2 class="font-exo-bold text-2xl text-white">11. Changes to This Privacy Policy</h2>
            <p>We may update this Privacy Policy from time to time. When we do:</p>
            <ul class="list-disc space-y-2 pl-6">
              <li>We’ll update the “Last Updated” date.</li>
              <li>Notify you by email or in-app announcement if changes are significant.</li>
              <li>Continued use after updates means you accept the revised policy.</li>
            </ul>
          </section>

          <!-- Contact Information -->
          <section class="space-y-4">
            <h2 class="font-exo-bold text-2xl text-white">12. Contact Us</h2>
            <p>For privacy inquiries or data requests:</p>
            <p class="font-segoe-bold text-white">CoreProc, Inc.</p>
            <div class="grid">
              <a href="mailto:hello@vibeteams.ai" class="flex w-fit items-center gap-1 no-underline"><Mail />hello@vibeteams.ai</a>
              <a href="https://vibeteams.ai" class="flex w-fit items-center gap-1 text-navy-blue-500 hover:underline"
                ><Globe /> https://vibeteams.ai</a
              >
            </div>
            <p class="mt-4">For European users, you may also contact your local data-protection authority.</p>
          </section>

          <!-- Summary -->
          <section class="space-y-4">
            <h2 class="font-exo-bold text-2xl text-white">13. Summary of Key Points</h2>
            <ul class="list-disc space-y-2 pl-6">
              <li>We collect account and usage data to operate the Service.</li>
              <li>AI interactions are processed securely by trusted third-party AI model providers.</li>
              <li>Payments are handled via third-party payment providers (PCI-DSS compliant).</li>
              <li>You can access, export, or delete your data at any time.</li>
              <li>We never sell personal information.</li>
              <li>We apply industry-standard security and encryption.</li>
            </ul>
          </section>

          <!-- AI Data Governance -->
          <section class="space-y-4">
            <h2 class="font-exo-bold text-2xl text-white">14. AI Data Governance</h2>
            <p>We maintain an internal AI Data Governance Framework to ensure that all AI operations:</p>
            <ul class="list-disc space-y-2 pl-6">
              <li>Use encryption and anonymization for stored and transmitted data</li>
              <li>Do not use customer content for model training without explicit consent</li>
              <li>Are audited regularly for privacy compliance and ethical standards</li>
              <li>Give users full control to delete chat data or opt out of analytics-based improvements</li>
            </ul>
          </section>
        </div>
      </div>
    </div>

    <!-- Footer -->
    <NavFooter />
  </div>
</template>

<script lang="ts">
import NavFooter from '@/components/landing/Footer.vue';
import { Head as InertiaHead, Link } from '@inertiajs/vue3';
import { Globe, Mail } from 'lucide-vue-next';

export default {
  components: {
    InertiaHead,
    NavFooter,
    InertiaLink: Link,
    Mail,
    Globe,
  },
  data() {
    return {
      isScrolled: false,
    };
  },
  mounted() {
    window.addEventListener('scroll', this.handleScroll);
  },
  unmounted() {
    window.removeEventListener('scroll', this.handleScroll);
  },
  methods: {
    handleScroll() {
      const scrolled = window.scrollY;
      const windowHeight = window.innerHeight;
      const documentHeight = document.documentElement.scrollHeight;
      const scrollPercent = (scrolled / (documentHeight - windowHeight)) * 100;

      this.isScrolled = scrollPercent > 1;
    },
  },
};
</script>

<style>
.bg-landing {
  background-image: url('@images/landing-page/landing-bg-asset.webp');
}
</style>
